import { Injectable } from '@angular/core';
import { BlocklyStorageService } from './blockly-storage.service';

/**
 * Zentrale Registry für Event-IDs aus EVENTS_ON_NEW-Blöcken.
 * Scannt alle Blockly-XMLs des aktuellen Bots und extrahiert die vergebenen Event-IDs.
 * Editor-übergreifend nutzbar (Singleton-Service).
 */
@Injectable({
    providedIn: 'root'
})
export class EventIdRegistryService {
    private _eventIds = new Map<string, string>(); // eventId -> uniqueId (Editor-Quelle)

    constructor(private blocklyStorage: BlocklyStorageService) { }

    /**
     * Scannt alle geladenen Blockly-XMLs nach EVENTS_ON_NEW-Blöcken
     * und extrahiert deren eventId-Felder.
     */
    public refresh(): void {
        this._eventIds.clear();
        for (const stage of this.blocklyStorage.stages) {
            this._extractEventIdsFromXml(stage.xml, stage.uniqueId);
        }
    }

    /**
     * Aktualisiert die Registry mit dem aktuellen XML eines einzelnen Editors.
     * Wird bei jeder Änderung aufgerufen.
     */
    public updateFromEditor(uniqueId: string, xml: string): void {
        // Entferne alte IDs dieses Editors
        for (const [eventId, sourceId] of this._eventIds.entries()) {
            if (sourceId === uniqueId) {
                this._eventIds.delete(eventId);
            }
        }
        // Extrahiere neue IDs
        this._extractEventIdsFromXml(xml, uniqueId);
    }

    /**
     * Gibt alle bekannten Event-IDs als Array zurück.
     */
    public getEventIds(): string[] {
        return Array.from(this._eventIds.keys());
    }

    /**
     * Gibt die Event-IDs als Blockly-Dropdown-Options zurück.
     * Format: [label, value][]
     */
    public getDropdownOptions(): [string, string][] {
        const ids = this.getEventIds();
        if (ids.length === 0) {
            return [['(keine IDs vergeben)', '']];
        }
        return ids.map(id => [id, id]);
    }

    private _extractEventIdsFromXml(xml: string, sourceUniqueId: string): void {
        if (!xml) return;
        // Suche nach EVENTS_ON_NEW-Blöcken und deren eventId-Feld
        const blockRegex = /<block[^>]*type="EVENTS_ON_NEW"[^>]*>([\s\S]*?)(?:<\/block>)/g;
        let blockMatch: RegExpExecArray | null;
        while ((blockMatch = blockRegex.exec(xml)) !== null) {
            const blockContent = blockMatch[1] ?? '';
            // eventId ist ein field_input mit name="eventId"
            const fieldRegex = /<field\s+name="eventId"[^>]*>([^<]*)<\/field>/;
            const fieldMatch = fieldRegex.exec(blockContent);
            if (fieldMatch?.[1]?.trim()) {
                this._eventIds.set(fieldMatch[1].trim(), sourceUniqueId);
            }
        }
    }
}
